import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useRef } from "react";

import AIDoctorIntakeFlow from "@/ai-doctor/AIDoctorIntakeFlow";
import {
  getConversation,
  useCreateConversation,
  useCreateRxEligibilityConversation,
} from "@/api/generated";
import {
  TextSendMessageRequestType,
  WorkflowType,
} from "@/api/generated/models";
import { usePostNoriMessage } from "@/api/mutations/conversation";
import { invalidateConversationHistory } from "@/api/queries/conversation";
import { LayoutContext } from "@/components/LayoutProvider";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import {
  MESSAGES_PER_PAGE,
  infiniteMessagesQueryKey,
} from "@/hooks/useInfiniteMessages";
import { usePartnerIntent } from "@/hooks/usePartnerIntent";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { setTag } from "@/sentry";
import { useUseCaseStore } from "@/store";
import { useRxQuestionnaireStore } from "@/store/rxQuestionnaire";
import type { EntryParams } from "@/utils/entryParams";

const RX_INITIAL_MESSAGES: Record<string, string> = {
  "weight-loss": "I'm interested in medication for weight loss",
  menopause: "I'm interested in HRT for menopause symptoms",
};

// Load the backend-seeded handoff turn into the messages cache before posting the user's message,
// so it renders above the (optimistically prepended) user message.
async function prefetchSeededHistory(
  queryClient: QueryClient,
  conversationId: string,
): Promise<void> {
  try {
    const firstPage = await getConversation(conversationId, {
      limit: MESSAGES_PER_PAGE,
    });
    queryClient.setQueryData(infiniteMessagesQueryKey(conversationId), {
      pages: [firstPage],
      pageParams: [null],
    });
  } catch {
    // ignore — the conversation page will fetch history on mount
  }
}

function getUseCaseTag(params: EntryParams): string {
  switch (params.action) {
    case "message":
      return params.useCase ?? "message";
    case "rx-eligibility":
      return params.condition;
    default:
      return params.action;
  }
}

export function useEntryAction() {
  const router = usePropagatedRouter();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const eventContext = useEventContext();
  const { setConversationUseCase } = useUseCaseStore();
  const { getLocalPartnerIntent } = usePartnerIntent();
  const { open: openRxQuestionnaire } = useRxQuestionnaireStore();
  const { setModalContent } = useContext(LayoutContext);

  const { mutate: createConversation } = useCreateConversation();
  const { mutate: createRxEligibilityConversation } =
    useCreateRxEligibilityConversation();
  const { mutate: postNoriMessage } = usePostNoriMessage();

  const hasExecutedRef = useRef(false);

  const execute = useCallback(
    (params: EntryParams, utmTerm?: string) => {
      if (hasExecutedRef.current) return;
      hasExecutedRef.current = true;

      setTag("context.use_case", getUseCaseTag(params));
      setTag("context.came_from_ads", Boolean(utmTerm));

      if (params.action === "message") {
        getLocalPartnerIntent().then((partnerIntent) => {
          createConversation(
            {
              data: {
                use_case: params.useCase,
                partner_intent: partnerIntent ?? null,
                source_conversation_id: params.sourceConversationId ?? null,
              },
            },
            {
              onSuccess: async (data) => {
                const conversationId = data?.conversation_id;

                invalidateConversationHistory(queryClient);

                track(AD_CONVERSION_EVENTS.conversationCreated, {
                  event_context: {
                    ...eventContext,
                    conversation_id: conversationId,
                    conversation_type: "nori",
                  },
                });

                if (params.useCase) {
                  setConversationUseCase(conversationId, params.useCase);
                }

                if (params.sourceConversationId) {
                  await prefetchSeededHistory(queryClient, conversationId);
                }

                router.replace(`/conversation/${conversationId}`);

                postNoriMessage({
                  message: params.msg,
                  conversationId,
                  agent: params.agent,
                  useCase: params.useCase,
                  utmTerm,
                });
              },
              onError: () => {
                router.replace("/");
              },
            },
          );
        });
      } else if (params.action === "rx-eligibility") {
        getLocalPartnerIntent().then((partnerIntent) => {
          createRxEligibilityConversation(
            {
              data: {
                initial_message: RX_INITIAL_MESSAGES[params.condition],
                partner_intent: partnerIntent ?? null,
              },
            },
            {
              onSuccess: ({ conversation_id }) => {
                track(AD_CONVERSION_EVENTS.conversationCreated, {
                  event_context: {
                    ...eventContext,
                    conversation_id,
                    conversation_type: "nori",
                  },
                });

                router.replace(`/conversation/${conversation_id}`);

                postNoriMessage({
                  message: "Yes",
                  conversationId: conversation_id,
                  type: TextSendMessageRequestType.rx_input,
                });

                setTimeout(() => {
                  openRxQuestionnaire(conversation_id);
                }, 300);
              },
              onError: () => {
                router.replace("/");
              },
            },
          );
        });
      } else if (params.action === "program") {
        getLocalPartnerIntent().then((partnerIntent) => {
          createConversation(
            {
              data: {
                program: params.program,
                partner_intent: partnerIntent ?? null,
              },
            },
            {
              onSuccess: (data) => {
                const conversationId = data?.conversation_id;

                invalidateConversationHistory(queryClient);

                track(AD_CONVERSION_EVENTS.conversationCreated, {
                  event_context: {
                    ...eventContext,
                    conversation_id: conversationId,
                    conversation_type: "nori",
                  },
                });

                // The request id stays in the URL until pre-clinic and enrollment open.
                const requestQuery = data?.clinic_request_id
                  ? `?clinic_request_id=${data.clinic_request_id}`
                  : "";
                router.replace(
                  `/conversation/${conversationId}${requestQuery}`,
                );
              },
              onError: () => {
                router.replace("/");
              },
            },
          );
        });
      } else if (
        params.action === "rx_renewal" ||
        params.action === "diagnosis"
      ) {
        const workflowType =
          params.action === "rx_renewal"
            ? WorkflowType.rx_renewal
            : WorkflowType.diagnosis;

        const onClose = () => setModalContent(null);
        setModalContent(() => () => (
          <AIDoctorIntakeFlow
            type={workflowType}
            onClose={onClose}
            hideCloseButton
          />
        ));
        router.replace("/");
      }
    },
    [
      createConversation,
      createRxEligibilityConversation,
      postNoriMessage,
      queryClient,
      track,
      eventContext,
      setConversationUseCase,
      getLocalPartnerIntent,
      openRxQuestionnaire,
      setModalContent,
      router,
    ],
  );

  return { execute };
}
